import { CommonModule } from '@angular/common';
import {
	AfterViewInit,
	Component,
	computed,
	effect,
	ElementRef,
	inject,
	input,
	OnDestroy,
	output,
	viewChild,
	ChangeDetectionStrategy,
} from '@angular/core';
import {
	ScatterChartConfig,
	isPaletteCustomOverridesActive,
} from '../../../models/chart-config.model';
import { ScatterChartData } from '../../../models/chart-data.model';
import { ThemeConfig } from '../../../models/theme.model';
import { DataService } from '../../../services/data.service';
import {
	highchartsPaletteForThemeMode,
	pinHighchartsPaletteForThemeMode,
} from '@framework/analytics/utils/highcharts-palette-for-theme.util';
import { resolveChartThemeMode } from '@framework/analytics/utils/resolve-chart-theme-mode.util';
import { PaletteService } from '../../../services/palette.service';
import { ThemeMode, ThemeService } from '../../../services/theme.service';
import Highcharts from '../highcharts.config';
import { scatterChartDefaults } from './jepto-scatter-chart.defaults';
import {
	resolveHighchartsCssColors,
	createHighchartsResizeObserver,
	reflowHighchartsToContainer,
	scheduleHighchartsReflowAfterLayout,
} from '../../../utils/resolve-highcharts-css-colors.util';
import { applyLegendLayoutToChartOptions } from '../../../utils/chart-legend-scale.util';
import { ChartRenderCoordinator } from '../../../utils/chart-render-coordinator.util';
import { parseChartVisualizationInput } from '../../../utils/parse-chart-visualization-input.util';

interface ScatterChartDataPoint {
	x: number;
	y: number;
	name?: string;
	color?: string;
}

interface ScatterChartSeries {
	name: string;
	data: ScatterChartDataPoint[];
	color?: string;
	label?: string;
	aggregationValue?: number;
	custom?: any;
}

interface ChartClickEvent {
	point: { x: number; y: number; name?: string; series: string };
	chart: { type: string; id?: string };
}

/**
 * Scatter Chart Component with signal-based reactivity
 */
@Component({
	selector: 'jepto-scatter-chart',
	templateUrl: './jepto-scatter-chart.component.html',
	styleUrls: ['./jepto-scatter-chart.component.scss'],
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [CommonModule],
})
export class JeptoScatterChartComponent implements AfterViewInit, OnDestroy {
	// Signal inputs
	cardId = input<string>();
	config = input<ScatterChartConfig>();
	data = input<ScatterChartData>();
	themeMode = input<ThemeMode>('auto');
	palette = input<string>('pal_jepto');
	themeConfig = input<ThemeConfig>();
	height = input<string>('100%');
	width = input<string>('100%');
	showLegend = input<boolean>(false);
	enableTooltip = input<boolean>(true);
	markerRadius = input<number>(6);
	showLabels = input<boolean>(false);

	// Outputs
	chartClick = output<ChartClickEvent>();
	pointClick = output<ChartClickEvent>();

	// View queries
	private chartContainer = viewChild.required<ElementRef>('chartContainer');

	// Services
	private themeService = inject(ThemeService);
	private paletteService = inject(PaletteService);
	private dataService = inject(DataService, { optional: true });

	// Internal state
	private chart?: Highcharts.Chart;
	private resizeObserver?: ResizeObserver;
	private initialized = false;
	private renderCoordinator = new ChartRenderCoordinator();

	// Computed signals
	private effectiveData = computed(() => {
		const direct = this.data();
		if (direct) return direct;

		const id = this.cardId();
		if (id && this.dataService) {
			const serviceData = this.dataService.cardDataSignal(id)();
			if ((serviceData as any)?.chartType === 'scatter')
				return serviceData as unknown as ScatterChartData;
		}
		return null;
	});

	private paletteColors = computed(() => {
		const cfg = this.config();
		const paletteId = cfg?.palette?.selectedPaletteId ?? this.palette();
		const resolvedMode = resolveChartThemeMode(
			this.themeMode(),
			this.themeService.getActiveThemeModeSignal()()
		);

		const overrides = isPaletteCustomOverridesActive(cfg?.palette);
		const customLight = overrides ? cfg?.palette?.customLight : undefined;
		const customDark = overrides ? cfg?.palette?.customDark : undefined;

		const palette = this.paletteService.getPaletteById(paletteId);
		if (palette) {
			if (resolvedMode === 'dark' && customDark?.length) {
				return customDark;
			}
			if (resolvedMode === 'light' && customLight?.length) {
				return customLight;
			}
			return resolvedMode === 'dark' ? palette.dark : palette.light;
		}

		if (cfg?.palette?.selectedPaletteId) {
			return this.paletteService.getColorsForPaletteWithOverrides(
				cfg.palette.selectedPaletteId,
				customLight,
				customDark,
				resolvedMode
			);
		}
		return this.paletteService.getColorPalette(paletteId);
	});

	private chartData = computed(() => {
		const sourceData = this.effectiveData();
		if (!sourceData?.data?.series) return [];

		return sourceData.data.series.map(
			(series: any) =>
				({
					name: series.name,
					data: series.data.map((point: any) => {
						if (Array.isArray(point))
							return {
								x: point[0],
								y: point[1],
								name: point[2] || undefined,
							};
						return point;
					}),
					color: series.color,
					label: series.label || series.name,
					aggregationValue: series.aggregationValue,
					custom: series.custom || {},
				}) as ScatterChartSeries
		);
	});

	private xAxisLabel = computed(() => {
		const sourceData = this.effectiveData();
		return sourceData?.data?.xAxisLabel || '';
	});

	private yAxisLabel = computed(() => {
		const sourceData = this.effectiveData();
		return sourceData?.data?.yAxisLabel || '';
	});

	private resolvedVisualization = computed(() => {
		const raw = this.config()?.visualization;
		const parsed = parseChartVisualizationInput(raw) as unknown;

		if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
			return parsed as Highcharts.Options;
		}

		return undefined;
	});

	private chartOptions = computed(() => {
		const baseOptions = { ...scatterChartDefaults };
		const colors = this.paletteColors();
		const cfg = this.config();
		const visualization = this.resolvedVisualization();
		const id = this.cardId();
		const radius = this.markerRadius();
		const labels = this.showLabels();

		this.themeConfig();
		const resolvedThemeMode = resolveChartThemeMode(
			this.themeMode(),
			this.themeService.getActiveThemeModeSignal()()
		);
		this.themeService.themeIdDisplaySignal();

		const componentOptions: Partial<Highcharts.Options> = {
			...highchartsPaletteForThemeMode(resolvedThemeMode),
			colors: colors?.length ? colors : undefined,
			chart: {
				...baseOptions.chart,
				backgroundColor: 'var(--chart-background, transparent)',
			},
			title: {
				text: cfg?.title || '',
				style: { color: 'var(--chart-text-color, inherit)' },
			},
			subtitle: {
				text: cfg?.subtitle || '',
				style: { color: 'var(--chart-text-color, inherit)' },
			},
			xAxis: {
				...baseOptions.xAxis,
				title: {
					text: this.xAxisLabel(),
					style: { color: 'var(--chart-text-color, inherit)' },
				},
			},
			yAxis: {
				...baseOptions.yAxis,
				title: {
					text: this.yAxisLabel(),
					style: { color: 'var(--chart-text-color, inherit)' },
				},
			},
			legend: { ...baseOptions.legend, enabled: this.showLegend() },
			tooltip: { ...baseOptions.tooltip, enabled: this.enableTooltip() },
			plotOptions: {
				...baseOptions.plotOptions,
				scatter: {
					...baseOptions.plotOptions?.scatter,
					marker: { radius, symbol: 'circle' },
					dataLabels: { enabled: labels },
					point: {
						events: {
							click: (event: any) => {
								const point = event.point;
								this.chartClick.emit({
									point: {
										x: point.x,
										y: point.y,
										name: point.name,
										series: point.series.name,
									},
									chart: { type: 'scatter', id },
								});
								this.pointClick.emit({
									point: {
										x: point.x,
										y: point.y,
										name: point.name,
										series: point.series.name,
									},
									chart: { type: 'scatter', id },
								});
							},
						},
					},
				},
			},
			series: this.transformDataToSeries(),
		};

		const seriesData = this.transformDataToSeries();
		let merged = Highcharts.merge(baseOptions, componentOptions);
		merged.series = seriesData;
		if (visualization) merged = Highcharts.merge(merged, visualization);
		return pinHighchartsPaletteForThemeMode(merged, resolvedThemeMode);
	});

	constructor() {
		effect(() => {
			const data = this.chartData();
			const options = this.chartOptions();
			if (this.initialized && this.chart && data.length > 0) {
				const chart = this.chart;
				this.renderCoordinator.schedule(() => {
					chart.destroy();
					this.renderChart(options);
				});
			}
		});
	}

	ngAfterViewInit(): void {
		this.initializeChart();
		this.setupResizeObserver();
		this.initialized = true;
	}

	ngOnDestroy(): void {
		this.renderCoordinator.cancel();
		this.resizeObserver?.disconnect();
		this.chart?.destroy();
	}

	private renderChart(options: Highcharts.Options): void {
		const container = this.chartContainer().nativeElement;
		const resolved = resolveHighchartsCssColors(
			applyLegendLayoutToChartOptions(options, container),
			container
		);
		this.chart = Highcharts.chart(container, resolved);
		reflowHighchartsToContainer(this.chart, container);
		scheduleHighchartsReflowAfterLayout(() => this.chart, container);
	}

	private initializeChart(): void {
		const container = this.chartContainer();
		if (container) {
			this.renderChart(this.chartOptions());
		}
	}

	private setupResizeObserver(): void {
		this.resizeObserver?.disconnect();
		this.resizeObserver = createHighchartsResizeObserver(
			() => this.chart,
			this.chartContainer().nativeElement
		);
	}

	private transformDataToSeries(): Highcharts.SeriesOptionsType[] {
		const inputData = this.chartData();
		if (!inputData?.length) return [];

		return inputData.map(
			series =>
				({
					type: 'scatter' as const,
					name: series.label || series.name,
					data: series.data,
					color: series.color || undefined,
					aggregationValue: series.aggregationValue,
					custom: series.custom,
				}) as any
		);
	}
}
